
(() => {
  const KEY = 'jaSavedArtworksV1';
  let savedDialogOpener = null;
  const PATTERNS = {
    handles: ['gift-wrapping','gift-wrap','handwritten-message','handwritten-note','personal-message','artwork-frame','painting-frame','frame-option'],
    handleFragments: ['gift-voucher','gift_voucher','giftvoucher'],
    titleFragments: ['gift voucher','gift card','gift wrapping','gift wrap','handwritten message','personal message','artwork frame','painting frame','frame option','commission request'],
    typeFragments: ['service','voucher','gift card','add-on','addon','commission','frame option'],
    tagFragments: ['service','gift-wrap','gift_wrapping','handwritten-message','frame-option','addon','add-on','commission','gift-voucher','giftvoucher']
  };

  const normalizeString = (value) => String(value || '').trim().toLowerCase();
  const listify = (value) => Array.isArray(value) ? value.map(String) : String(value || '').split('|').filter(Boolean);
  const includesAny = (value, values) => values.some((entry) => value.includes(entry));
  const isEligibleArtwork = (item) => {
    if (!item) return false;
    if (String(item.eligibleArtwork || '').toLowerCase() === 'true') return true;
    if (String(item.eligibleArtwork || '').toLowerCase() === 'false') return false;
    const handle = normalizeString(item.handle || (String(item.url || '').match(/\/products\/([^/?#]+)/i) || [,''])[1]);
    const title = normalizeString(item.title);
    const type = normalizeString(item.productType || item.type);
    const tags = listify(item.tags).map(normalizeString).join('|');
    if (PATTERNS.handles.includes(handle)) return false;
    if (includesAny(handle, PATTERNS.handleFragments)) return false;
    if (includesAny(title, PATTERNS.titleFragments)) return false;
    if (includesAny(type, PATTERNS.typeFragments)) return false;
    if (includesAny(tags, PATTERNS.tagFragments)) return false;
    return true;
  };

  const read = () => {
    try {
      const value = JSON.parse(localStorage.getItem(KEY) || '[]');
      return (Array.isArray(value) ? value : []).filter(isEligibleArtwork);
    } catch (_) {
      return [];
    }
  };

  const write = (items) => {
    try { localStorage.setItem(KEY, JSON.stringify((items || []).filter(isEligibleArtwork))); } catch (_) {}
  };

  const escapeHtml = (value) => String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const itemFromButton = (button) => ({
    id: String(button.dataset.productId || ''),
    title: button.dataset.productTitle || '',
    url: button.dataset.productUrl || '#',
    image: button.dataset.productImage || '',
    price: button.dataset.productPrice || '',
    available: button.dataset.productAvailable !== 'false',
    handle: button.dataset.productHandle || '',
    productType: button.dataset.productType || '',
    tags: button.dataset.productTags ? button.dataset.productTags.split('|') : [],
    eligibleArtwork: button.dataset.productArtworkEligible || ''
  });

  const updateButtons = () => {
    const ids = new Set(read().map(item => String(item.id)));
    document.querySelectorAll('[data-ja-save-artwork]').forEach(button => {
      const saved = ids.has(String(button.dataset.productId));
      button.setAttribute('aria-pressed', saved ? 'true' : 'false');
      const label = button.querySelector('.ja-save-artwork__label');
      if (label) label.textContent = saved ? 'Saved' : 'Save artwork';
      button.setAttribute('aria-label', `${saved ? 'Remove' : 'Save'} ${button.dataset.productTitle || 'artwork'}`);
    });

    document.querySelectorAll('[data-ja-saved-count]').forEach(count => {
      const length = ids.size;
      count.textContent = length > 0 ? String(length) : '';
      count.hidden = length === 0;
      count.style.display = length > 0 ? 'grid' : 'none';
    });
  };

  const productJsonUrl = (itemUrl) => {
    const url = new URL(itemUrl, window.location.origin);
    url.pathname = url.pathname.replace(/\/$/, '') + '.js';
    url.search = '';
    return url.toString();
  };

  const refreshSavedAvailability = async () => {
    const items = read();
    if (!items.length) return items;

    const refreshed = await Promise.all(items.map(async item => {
      try {
        const response = await fetch(productJsonUrl(item.url), { credentials: 'same-origin' });
        if (response.status === 404 || response.status === 410) return null;
        if (!response.ok) return item;
        const product = await response.json();
        return {
          ...item,
          available: Boolean(product.available),
          handle: product.handle || item.handle,
          productType: product.type || item.productType,
          tags: product.tags || item.tags,
          eligibleArtwork: String(isEligibleArtwork({
            handle: product.handle || item.handle,
            title: product.title || item.title,
            productType: product.type || item.productType,
            tags: product.tags || item.tags,
            url: item.url
          }))
        };
      } catch (_) {
        return item;
      }
    }));

    const validItems = refreshed.filter(Boolean);
    write(validItems);
    return validItems;
  };

  const renderModal = (items = read()) => {
    const modal = document.querySelector('[data-ja-saved-modal]');
    const grid = modal?.querySelector('[data-ja-saved-grid]');
    const empty = modal?.querySelector('[data-ja-saved-empty]');
    if (!modal || !grid || !empty) return;

    empty.hidden = items.length > 0;
    grid.hidden = items.length === 0;

    grid.innerHTML = items.map(item => `
      <article class="ja-saved-card${item.available === false ? ' is-sold' : ''}">
        <a class="ja-saved-card__media" href="${escapeHtml(item.url)}">
          ${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" loading="lazy">` : '<span class="ja-saved-card__placeholder" aria-hidden="true"></span>'}
          ${item.available === false ? '<span class="ja-saved-card__sold">SOLD</span>' : ''}
        </a>
        <div class="ja-saved-card__meta">
          <a href="${escapeHtml(item.url)}">${escapeHtml(item.title)}</a>
          <span>${item.available === false ? '<strong class="ja-saved-card__meta-sold">SOLD</strong>' : escapeHtml(item.price)}</span>
          <button type="button" class="ja-saved-card__remove" data-ja-remove-saved="${escapeHtml(item.id)}" aria-label="Remove ${escapeHtml(item.title || 'artwork')} from saved works">Remove</button>
        </div>
      </article>
    `).join('');
  };

  document.addEventListener('click', (event) => {
    const saveButton = event.target.closest('[data-ja-save-artwork]');
    if (saveButton) {
      event.preventDefault();
      event.stopPropagation();

      const item = itemFromButton(saveButton);
      if (!isEligibleArtwork(item)) {
        updateButtons();
        return;
      }
      let items = read();
      const exists = items.some(saved => String(saved.id) === item.id);
      items = exists
        ? items.filter(saved => String(saved.id) !== item.id)
        : [item, ...items.filter(saved => String(saved.id) !== item.id)].slice(0, 50);

      write(items);
      updateButtons();
      renderModal();
      return;
    }

    const openButton = event.target.closest('[data-ja-saved-open]');
    if (openButton) {
      event.preventDefault();
      renderModal();
      savedDialogOpener = openButton;
      document.querySelector('[data-ja-saved-modal]')?.showModal();
      refreshSavedAvailability().then(items => {
        renderModal(items);
        updateButtons();
      });
      return;
    }

    const closeButton = event.target.closest('[data-ja-saved-close]');
    if (closeButton) {
      document.querySelector('[data-ja-saved-modal]')?.close();
      return;
    }

    const removeButton = event.target.closest('[data-ja-remove-saved]');
    if (removeButton) {
      const id = String(removeButton.dataset.jaRemoveSaved || '');
      write(read().filter(item => String(item.id) !== id));
      updateButtons();
      renderModal();
    }
  });

  document.addEventListener('DOMContentLoaded', () => {
    updateButtons();
    renderModal();

    const modal = document.querySelector('[data-ja-saved-modal]');
    modal?.addEventListener('click', (event) => {
      if (event.target === modal) modal.close();
    });
    modal?.addEventListener('close', () => {
      if (savedDialogOpener?.isConnected) savedDialogOpener.focus({ preventScroll: true });
      savedDialogOpener = null;
    });

    // Horizon can inject product cards after initial load. Only resync when a
    // newly-added element actually contains a Save artwork control. This avoids
    // the previous observer loop where our own label/count updates retriggered
    // the observer indefinitely on product pages.
    const observer = new MutationObserver((mutations) => {
      const hasNewSaveControl = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some((node) => {
          if (!(node instanceof Element)) return false;
          return node.matches?.('[data-ja-save-artwork]') || Boolean(node.querySelector?.('[data-ja-save-artwork]'));
        })
      );
      if (hasNewSaveControl) updateButtons();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  });
})();
